import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faCopy } from '@fortawesome/free-solid-svg-icons';
import HighlightedText, { countMatches } from './HighlightedText';

const RequestViewer = ({ exchange, isDarkMode, searchRegex, activeMatchIndex, matchOffset = 0, onMatchCount, viewMode: viewModeProp, onViewModeChange }) => {
  const [viewModeState, setViewModeState] = useState('pretty');
  const rawViewMode = viewModeProp || viewModeState;
  const setViewMode = (m) => (onViewModeChange ? onViewModeChange(m) : setViewModeState(m));
  // Request has no 'render' mode — fall back to pretty when the shared view is 'render'
  const viewMode = rawViewMode === 'render' ? 'pretty' : rawViewMode;
  const request = exchange?.request;

  // Cap rendered body: highlighting/formatting a multi-MB body (e.g. a file upload)
  // freezes the browser. Show the first 200 KB with a truncation notice.
  const MAX_BODY = 200 * 1024;
  const rawBody = request?.body || '';
  const bodyTooBig = rawBody.length > MAX_BODY;
  const displayBody = bodyTooBig ? rawBody.slice(0, MAX_BODY) : rawBody;
  const truncNote = bodyTooBig ? `\n\n… [body truncated — showing first ${Math.round(MAX_BODY / 1024)} KB of ${(rawBody.length / 1048576).toFixed(1)} MB]` : '';

  const buildRawRequest = () => {
    if (!request) return '';
    let raw = `${request.method} ${request.path || new URL(request.url).pathname}${new URL(request.url).search} HTTP/1.1\r\n`;
    raw += `Host: ${new URL(request.url).host}\r\n`;
    for (const h of (request.headers || [])) {
      if (h.name.toLowerCase() !== 'host') {
        raw += `${h.name}: ${h.value}\r\n`;
      }
    }
    raw += '\r\n';
    if (rawBody) raw += displayBody + truncNote;
    return raw;
  };

  // Get the displayed text for search counting
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const displayedText = useMemo(() => {
    if (!request) return '';
    if (viewMode === 'raw') return buildRawRequest();
    if (viewMode === 'headers') {
      return (request.headers || []).map(h => `${h.name}: ${h.value}`).join('\n');
    }
    // pretty mode — show URL + query params + body
    let text = `${request.method} ${request.url}\n`;
    if (request.queryParams?.length > 0) {
      text += '\nQuery Parameters:\n';
      request.queryParams.forEach(p => { text += `${p.key}: ${p.value}\n`; });
    }
    if (rawBody) {
      text += '\nBody:\n';
      if (bodyTooBig) text += displayBody + truncNote;
      else { try { text += JSON.stringify(JSON.parse(rawBody), null, 2); } catch { text += rawBody; } }
    }
    return text;
  }, [request, viewMode, rawBody, bodyTooBig, displayBody, truncNote]);

  // Count and report matches
  const localMatchCount = useMemo(() => countMatches(displayedText, searchRegex), [displayedText, searchRegex]);

  useEffect(() => {
    if (onMatchCount) onMatchCount(localMatchCount);
  }, [localMatchCount, onMatchCount]);

  if (!request) {
    return (
      <div className={`p-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Select an exchange to view the request.
      </div>
    );
  }

  const preClass = `text-xs font-mono whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className="h-full flex flex-col">
      <div className={`flex items-center justify-between px-3 py-1.5 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faArrowUp} className="text-blue-400 text-xs" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Request</span>
        </div>
        <div className="flex items-center gap-1">
          {['pretty', 'raw', 'headers'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-2 py-0.5 text-[10px] rounded ${
                viewMode === mode
                  ? (isDarkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-100 text-teal-700')
                  : (isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
          <button
            onClick={() => navigator.clipboard.writeText(buildRawRequest())}
            className={`p-1 rounded ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
            title="Copy raw request"
          >
            <FontAwesomeIcon icon={faCopy} className="text-[10px]" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {viewMode === 'raw' && (
          <HighlightedText
            text={buildRawRequest()}
            searchRegex={searchRegex}
            matchOffset={matchOffset}
            activeMatchIndex={activeMatchIndex}
            className={preClass}
          />
        )}

        {viewMode === 'pretty' && (
          <div className="space-y-3">
            <div>
              <span className={`font-mono text-xs font-bold ${
                request.method === 'GET' ? 'text-green-500' :
                request.method === 'POST' ? 'text-yellow-500' :
                request.method === 'DELETE' ? 'text-red-500' : 'text-blue-500'
              }`}>
                {request.method}
              </span>
              <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{request.url}</span>
            </div>
            {request.queryParams?.length > 0 && (
              <div>
                <div className={`text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Query Parameters</div>
                <table className="w-full text-xs">
                  <tbody>
                    {request.queryParams.map((p, i) => (
                      <tr key={i} className={isDarkMode ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                        <td className={`py-0.5 pr-3 font-medium ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{p.key}</td>
                        <td className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{p.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {rawBody && (
              <div>
                <div className={`text-[10px] font-medium mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Body</div>
                <HighlightedText
                  text={bodyTooBig ? (displayBody + truncNote) : (() => { try { return JSON.stringify(JSON.parse(rawBody), null, 2); } catch { return rawBody; } })()}
                  searchRegex={searchRegex}
                  matchOffset={matchOffset}
                  activeMatchIndex={activeMatchIndex}
                  className={`text-xs font-mono whitespace-pre-wrap p-2 rounded ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                />
              </div>
            )}
          </div>
        )}

        {viewMode === 'headers' && (
          <table className="w-full text-xs">
            <tbody>
              {(request.headers || []).map((h, i) => (
                <tr key={i} className={isDarkMode ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                  <td className={`py-0.5 pr-4 font-medium whitespace-nowrap ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{h.name}</td>
                  <td className={`py-0.5 break-all ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{h.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RequestViewer;
